import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getRestaurantId, menuItemsAPI, ordersAPI, restaurantAPI, waiterAPI } from '../utils/api';
import { subscribeRestaurantRealtime, unsubscribeChannel } from '../utils/realtime';

const AdminRealtimeDataContext = createContext(null);

export function AdminRealtimeDataProvider({ children }) {
  const [restaurant, setRestaurant] = useState(null);
  const [restaurantForm, setRestaurantForm] = useState(null);
  const [stats, setStats] = useState({ totalMenuItems: 0, totalOrders: 0, pendingOrders: 0 });
  const [waiterCalls, setWaiterCalls] = useState([]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const realtimeChannelRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const waiterPollRef = useRef(null);

  const hydrateRestaurantForm = useCallback((r) => {
    setRestaurantForm({
      name: r?.name || '',
      phone: r?.phone || '',
      address: r?.address || '',
      location: r?.location || '',
      numberOfTables: r?.number_of_tables ?? r?.numberOfTables ?? '',
      wifiOnlyEnabled: !!r?.wifi_only_enabled,
      wifiAllowedCidrs: r?.wifi_allowed_cidrs || '',
      gstNumber: r?.gst_number || '',
      gstRatePercent: r?.gst_rate_percent ?? '',
      upiId: r?.upi_id || '',
    });
  }, []);

  const refreshStats = useCallback(async () => {
    const restaurantId = getRestaurantId();
    if (!restaurantId) return;

    const [menuItemsResponse, ordersResponse, pendingOrdersResponse] = await Promise.all([
      menuItemsAPI.getAll(restaurantId),
      ordersAPI.getAll(),
      ordersAPI.getPending(),
    ]);

    setStats({
      totalMenuItems: menuItemsResponse.data.menuItems?.length || 0,
      totalOrders: ordersResponse.data.orders?.length || 0,
      pendingOrders: pendingOrdersResponse.data.orders?.length || 0,
    });
  }, []);

  const refreshWaiterCalls = useCallback(async () => {
    const res = await waiterAPI.getPending();
    const calls = res.data.calls || [];
    setWaiterCalls(calls);
    return calls;
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshStats(), refreshWaiterCalls()]);
    setLastUpdatedAt(Date.now());
  }, [refreshStats, refreshWaiterCalls]);

  const ensureRestaurantLoaded = useCallback(async () => {
    const restaurantResponse = await restaurantAPI.getCurrent();
    const r = restaurantResponse.data.restaurant;
    setRestaurant(r);
    hydrateRestaurantForm(r);
    return r;
  }, [hydrateRestaurantForm]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const startedAt = Date.now();
      const MIN_LOADER_MS = 3500; // 3–4 seconds

      try {
        setInitialLoading(true);
        setError(null);

        const r = await ensureRestaurantLoaded();
        if (cancelled) return;

        // Load stats/waiter calls in background (but still part of first hydration)
        await refreshAll();
        if (cancelled) return;

        // Start waiter polling fallback (only once for entire app)
        waiterPollRef.current = setInterval(() => {
          refreshWaiterCalls().catch(() => {});
        }, 10000);

        // Start realtime subscription once
        const restaurantId = getRestaurantId();
        if (restaurantId) {
          const scheduleRefresh = () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => {
              refreshAll().catch(() => {});
            }, 400);
          };

          realtimeChannelRef.current = subscribeRestaurantRealtime({
            restaurantId,
            tables: ['orders', 'menu_items', 'waiter_calls'],
            onChange: scheduleRefresh,
            channelName: `rt:admin:${restaurantId}`,
          });
        }

        // Keep loader visible for a minimum duration for smoother UX
        const elapsed = Date.now() - startedAt;
        const remaining = MIN_LOADER_MS - elapsed;
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }

        setInitialLoading(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('AdminRealtimeDataProvider init error:', e);
        setError(e);
        setInitialLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (waiterPollRef.current) clearInterval(waiterPollRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      unsubscribeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    };
  }, [ensureRestaurantLoaded, refreshAll, refreshWaiterCalls]);

  const value = useMemo(
    () => ({
      restaurant,
      setRestaurant,
      restaurantForm,
      setRestaurantForm,
      hydrateRestaurantForm,
      stats,
      waiterCalls,
      initialLoading,
      error,
      lastUpdatedAt,
      refreshAll,
      refreshStats,
      refreshWaiterCalls,
    }),
    [
      restaurant,
      restaurantForm,
      hydrateRestaurantForm,
      stats,
      waiterCalls,
      initialLoading,
      error,
      lastUpdatedAt,
      refreshAll,
      refreshStats,
      refreshWaiterCalls,
    ]
  );

  return <AdminRealtimeDataContext.Provider value={value}>{children}</AdminRealtimeDataContext.Provider>;
}

export function useAdminRealtimeData() {
  const ctx = useContext(AdminRealtimeDataContext);
  if (!ctx) throw new Error('useAdminRealtimeData must be used within AdminRealtimeDataProvider');
  return ctx;
}
